import React, { useState, useEffect } from 'react';

const APP_STORE_URL = process.env.REACT_APP_APP_STORE_URL || '';
const PLAY_STORE_URL = process.env.REACT_APP_PLAY_STORE_URL || '';

/**
 * Detect user's platform from user agent for showing the right store link.
 * Only used on web — native app users never see this modal.
 */
const detectPlatform = () => {
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod|Macintosh/i.test(ua)) return 'apple';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
};

const AppDownloadModal = ({
  isOpen,
  onClose,
  title = 'Get the RideHowl App',
  subtitle = 'Download the app for quick access to your rides, real-time notifications, and a smoother experience on the go.',
  iosOnly = false,
  dismissLabel = 'Continue on web',
}) => {
  const [platform, setPlatform] = useState('desktop');

  useEffect(() => {
    setPlatform(detectPlatform());
  }, []);

  if (!isOpen) return null;

  const hasApple = !!APP_STORE_URL;
  const hasAndroid = !iosOnly && !!PLAY_STORE_URL;

  // Don't show if no applicable store URLs are configured
  if (!hasApple && !hasAndroid) {
    onClose();
    return null;
  }

  const showApple = hasApple && (platform === 'apple' || platform === 'desktop');
  const showAndroid = hasAndroid && (platform === 'android' || platform === 'desktop');

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full p-8 text-center">
          {/* App icon */}
          <img
            src="/logo.png"
            alt="RideHowl"
            className="w-20 h-20 mx-auto mb-4 rounded-2xl shadow-md"
          />

          <h2 className="text-xl font-bold text-gray-900 mb-2">
            {title}
          </h2>
          <p className="text-gray-600 mb-6">
            {subtitle}
          </p>

          <div className="space-y-3">
            {showApple && (
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 w-full bg-black text-white py-3 px-6 rounded-xl font-medium hover:bg-gray-800 transition"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
                Download on the App Store
              </a>
            )}

            {showAndroid && (
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 w-full bg-[#01875f] text-white py-3 px-6 rounded-xl font-medium hover:bg-[#017a55] transition"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3.609 1.814L13.792 12 3.609 22.186a.996.996 0 0 1-.609-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.302 2.302a1 1 0 0 1 0 1.38l-2.302 2.302L15.3 13l2.398-3.492zM5.864 2.658L16.8 8.99l-2.302 2.302L5.864 2.658z" />
                </svg>
                Get it on Google Play
              </a>
            )}
          </div>

          <button
            onClick={onClose}
            className="mt-5 text-gray-500 hover:text-gray-700 text-sm font-medium transition"
          >
            {dismissLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppDownloadModal;
